import _get from 'lodash/get';
import _map from 'lodash/map';
import _mapValues from 'lodash/mapValues';
import {
  CreativeTemplate,
  CreativeTemplateFeature,
  CreativeTemplateType,
  DynamicParamNameOptions,
  Format,
  FormatInstance,
  GeneratedDynamicParams,
  RemoteCreativeTemplate,
  RemoteDynamicParamNameOptions,
  RemoteFormat,
  RemoteFormatInstance,
  RemoteGeneratedDynamicParams,
  templateTypeNamesByValue,
} from './creativeTemplates.types';
import {toClientDynamicParameters} from '../creatives/creatives.dataConverters';
import mapValues from 'lodash/mapValues';
import {DateTime} from 'luxon';
import {RemoteTemplateAsset, TemplateAsset} from '../dynamicParameters/dynamicParameters.types';
import ObjectStatus from '../enums/static/ObjectStatus';
import {RemoteTemplateLogItem, TemplateLogItem} from './creativeTemplateActivityLog.types';
import {CLEnumsMapper} from '../enums/enums.types';

function toClientFormatInstance(remoteFormatInstance: RemoteFormatInstance): FormatInstance {
  return {
    ...remoteFormatInstance,
    formatString: `${remoteFormatInstance.Width}x${remoteFormatInstance.Height}`,
  };
}

function toClientDynamicParamsNameOptions(
  remoteNames: RemoteDynamicParamNameOptions,
): DynamicParamNameOptions {
  return mapValues(remoteNames, (options) =>
    options.map(({Type, Name}) => ({type: Type, label: Name, value: Name})),
  );
}

function toClientFormat(remoteFormat: RemoteFormat): Format {
  return {
    ...remoteFormat,
    format: toClientFormatInstance(remoteFormat.format),
  };
}

function toClientCreativeTemplate(
  remoteTemplate: RemoteCreativeTemplate,
  templateEnums: Record<string, CLEnumsMapper>,
): CreativeTemplate {
  return {
    ...remoteTemplate,
    formats: _map(remoteTemplate.formats, toClientFormat),
    minItemsCount: _get(remoteTemplate, 'formats[0].minItemCount', 0),
    maxItemsCount: _get(remoteTemplate, 'formats[0].itemCount', 0),
    typeName: templateTypeNamesByValue[remoteTemplate.type as CreativeTemplateType],
    isXenon: remoteTemplate.features?.includes(CreativeTemplateFeature.Xenon) ?? false,
    dynamicParameters: toClientDynamicParameters(remoteTemplate.dynamicParameters, templateEnums),
    creativeIdInActiveCampaigns: remoteTemplate.creativeIdInActiveCampaigns || {},
    ...(remoteTemplate.updateTime && {updateTime: DateTime.fromISO(remoteTemplate.updateTime)}),
    ...(remoteTemplate.createTime && {createTime: DateTime.fromISO(remoteTemplate.createTime)}),
  };
}

function toClientTemplateAsset(remoteTemplateAsset: RemoteTemplateAsset): TemplateAsset {
  return {
    id: remoteTemplateAsset.filename,
    isFromLibrary: false,
    status: ObjectStatus.Active,
    metadata: remoteTemplateAsset.metadata,
  };
}

function toClientActivityLog(remoteActivityLog: RemoteTemplateLogItem): TemplateLogItem {
  return {
    ...remoteActivityLog,
    sourceTemplateId: remoteActivityLog.sourceTemplateId ?? 0,
    actionTime: DateTime.fromISO(remoteActivityLog.actionTime),
  };
}

function toClientGeneratedDynamicParamsByFormat(
  dynamicParametersPerFormat: Record<string, RemoteGeneratedDynamicParams>,
): Record<string, GeneratedDynamicParams[]> {
  const result: Record<string, GeneratedDynamicParams[]> = {};

  _mapValues(
    dynamicParametersPerFormat,
    (dynamicParameters: RemoteGeneratedDynamicParams, _originalKey: string) => {
      // Group layers by format
      const layersByFormat = dynamicParameters.layers.reduce(
        (acc: Record<string, typeof dynamicParameters.layers>, layer) => {
          if (!acc[layer.format]) {
            acc[layer.format] = [];
          }

          acc[layer.format].push(layer);

          return acc;
        },
        {} as Record<string, typeof dynamicParameters.layers>,
      );

      // For each format that has layers for this dynamic parameter
      Object.entries(layersByFormat).forEach(([format, layersInFormat]) => {
        if (!result[format]) {
          result[format] = [];
        }

        // Create a modified dynamic parameter with only the layerIds for this format
        const dynamicParamForFormat: GeneratedDynamicParams = {
          dynamicParameter: dynamicParameters.dynamicParameter,
          layerIds: layersInFormat.map(
            ({layerId, compId, mappingId}: {layerId: number; compId: number; mappingId: number}) =>
              `${compId}-${layerId}-${mappingId}`,
          ),
        };

        result[format].push(dynamicParamForFormat);
      });
    },
  );

  return result;
}

export {
  toClientCreativeTemplate,
  toClientDynamicParamsNameOptions,
  toClientTemplateAsset,
  toClientActivityLog,
  toClientGeneratedDynamicParamsByFormat,
};
